import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import { getChatHistory, uploadChatFile } from '../../services/patientService';
import { useCall } from '../../context/CallContext';

function Bubble({ msg, isMe }) {
  const t = new Date(msg.timestamp || msg.createdAt)
    .toLocaleTimeString('en',{hour:'2-digit',minute:'2-digit',hour12:true});
  return (
    <div className={`flex mb-2 ${isMe ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[78%] rounded-2xl px-3 py-2 ${isMe
        ? 'bg-primary text-white rounded-br-sm'
        : 'bg-white text-text-primary rounded-bl-sm shadow-sm border border-border'}`}>
        {msg.type === 'text'   && <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.message}</p>}
        {msg.type === 'image'  && <img src={msg.fileUrl} alt="img" className="rounded-xl max-w-full max-h-56 object-cover cursor-pointer" onClick={() => window.open(msg.fileUrl,'_blank')} />}
        {msg.type === 'pdf'    && <a href={msg.fileUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2"><i className="fa-solid fa-file-pdf text-danger text-2xl" /><span className="text-sm underline">{msg.fileName||'Document.pdf'}</span></a>}
        {msg.type === 'voice'  && <audio controls src={msg.fileUrl} className="h-8 max-w-full" />}
        {msg.type === 'system' && <p className="text-xs italic opacity-80">{msg.message}</p>}
        <p className={`text-[9px] mt-1 text-right ${isMe ? 'text-white/70' : 'text-text-muted'}`}>
          {t}{isMe && <i className={`fa-solid fa-check-double ml-1 ${msg.isRead ? 'text-blue-300':'opacity-50'}`} />}
        </p>
      </div>
    </div>
  );
}

function DateSep({ date }) {
  return (
    <div className="flex items-center gap-2 my-3">
      <div className="flex-1 h-px bg-border" />
      <span className="text-[10px] text-text-muted px-2">
        {new Date(date).toLocaleDateString('en',{weekday:'long',day:'numeric',month:'short'})}
      </span>
      <div className="flex-1 h-px bg-border" />
    </div>
  );
}

export default function MedicineChatPage() {
  const { doctorId }   = useParams();
  const navigate       = useNavigate();
  const location       = useLocation();
  const { user }       = useAuth();
  const { socket }     = useSocket();

  const staffName  = location.state?.staffName  || 'Medicine Store';
  const doctorName = location.state?.doctorName || '';
  const myId       = user?._id?.toString() || '';
  // storeId is the socket room the doctor/store listens on
  const storeId    = `store_${doctorId}`;
  // conversationId is deterministic — same on both sides
  const conversationId = [myId, storeId].sort().join('_');

  // FIX: patient calls doctorId directly (doctor's socket room)
  // Doctor hears incoming-call {from: myId} and their rtc remoteId = myId (patientId)
  const rtc = useCall(); // single SHARED global call engine

  // NOTE: page-ownership for incoming calls is now handled automatically by
  // useCallRTC itself via window.__RTC_PAGE_OWNERS (registers remoteId on
  // mount). No manual flag needed here anymore.

  const [messages,   setMessages]   = useState([]);
  const [text,       setText]       = useState('');
  const [loading,    setLoading]    = useState(true);
  const [uploading,  setUploading]  = useState(false);
  const [theyTyping, setTheyTyping] = useState(false);
  const [showAttach, setShowAttach] = useState(false);

  const bottomRef   = useRef(null);
  const typingTimer = useRef(null);
  const fileRef     = useRef(null);
  const isTypingRef = useRef(false); // prevent self-echo on typing

  // Join conversation room
  useEffect(() => {
    if (!socket || !conversationId) return;
    socket.emit('join-conversation', { conversationId });
    return () => socket.emit('leave-conversation', { conversationId });
  }, [socket, conversationId]);

  // Load history
  const loadHistory = useCallback(async () => {
    if (!myId) return;
    setLoading(true);
    try {
      const res = await getChatHistory(conversationId, { limit: 60 });
      setMessages(res.messages || []);
    } catch {}
    finally { setLoading(false); }
  }, [conversationId, myId]);

  useEffect(() => { if (myId) loadHistory(); }, [loadHistory, myId]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior:'smooth' }); }, [messages]);

  // Real-time socket events
  useEffect(() => {
    if (!socket) return;

    const onReceive = (msg) => {
      if (msg.conversationId !== conversationId) return;
      // Ignore own messages (already in state optimistically)
      if (msg.from === myId || msg.senderId === myId) return;
      setMessages(p => {
        if (p.some(m => m._id === msg._id)) return p;
        return [...p, { ...msg, isRead: false }];
      });
      socket.emit('mark-read', { conversationId });
    };

    // FIX: typing comes from doctorId OR storeId (backend emits from:userId which is doctorId)
    const onTyping   = ({ from }) => {
      if (from === doctorId || from === storeId) {
        if (!isTypingRef.current) setTheyTyping(true);
      }
    };
    const onStopType = ({ from }) => {
      if (from === doctorId || from === storeId) setTheyTyping(false);
    };
    const onRead = () => setMessages(p => p.map(m => ({ ...m, isRead: true })));

    socket.on('receive-message',     onReceive);
    socket.on('user-typing',         onTyping);
    socket.on('user-stopped-typing', onStopType);
    socket.on('messages-read',       onRead);
    socket.emit('mark-read', { conversationId });

    return () => {
      socket.off('receive-message',     onReceive);
      socket.off('user-typing',         onTyping);
      socket.off('user-stopped-typing', onStopType);
      socket.off('messages-read',       onRead);
    };
  }, [socket, conversationId, myId, doctorId, storeId]);

  const sendMessage = () => {
    if (!text.trim() || !socket || !myId) return;
    const msg = {
      _id: `temp_${Date.now()}`, conversationId,
      from: myId, senderId: myId,
      message: text, type: 'text',
      timestamp: new Date().toISOString(), isRead: false,
    };
    setMessages(p => [...p, msg]);
    // FIX: send to storeId (doctor's store room) not doctorId
    socket.emit('send-message', { to: storeId, message: text, type:'text', senderRole:'patient', conversationId });
    setText(''); setShowAttach(false);
  };

  const handleTextChange = (val) => {
    setText(val);
    if (socket) {
      isTypingRef.current = true;
      // FIX: emit typing to storeId
      socket.emit('typing', { to: storeId, conversationId });
      clearTimeout(typingTimer.current);
      typingTimer.current = setTimeout(() => {
        isTypingRef.current = false;
        socket.emit('stop-typing', { to: storeId, conversationId });
      }, 1500);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    setUploading(true); setShowAttach(false);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('receiverId', storeId);
      const res = await uploadChatFile(fd);
      const newMsg = { ...res.message, from: myId, senderId: myId, timestamp: res.message?.createdAt || new Date().toISOString() };
      setMessages(p => {
        if (p.some(m => m._id === newMsg._id)) return p;
        return [...p, newMsg];
      });
    } catch (err) { toast.error('Upload failed: ' + err.message); }
    finally { setUploading(false); }
  };

  const grouped = messages.reduce((acc, msg) => {
    const d = new Date(msg.timestamp || msg.createdAt).toDateString();
    if (!acc[d]) acc[d] = [];
    acc[d].push(msg);
    return acc;
  }, {});

  return (
    <div className="flex flex-col bg-white w-full"
      style={{height:"100dvh",maxWidth:"768px",margin:"0 auto",borderLeft:"1px solid #E2E8F0",borderRight:"1px solid #E2E8F0"}}>

      {/* Header */}
      <div className="sticky top-0 z-30 bg-white border-b border-border flex-shrink-0">
        <div className="flex items-center gap-3 px-4 h-14">
          <button onClick={() => navigate(-1)} className="btn-icon">
            <i className="fa-solid fa-arrow-left text-text-primary" />
          </button>
          <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
            <i className="fa-solid fa-pills text-green-600 text-sm" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-text-primary">Medicine Store</p>
            <p className="text-xs">
              {theyTyping
                ? <span className="text-success font-medium">typing...</span>
                : <span className="text-text-muted">{doctorName ? `Dr. ${doctorName}` : staffName}</span>}
            </p>
          </div>
          {/* Voice call button — patient can call medicine store */}
          {rtc.callState === 'idle' && (
            <button
              onClick={() => rtc.startCall(
                'audio',
                user?.name || 'Patient',                                    // sent to the store as MY name
                doctorId,
                true,
                doctorName ? `Dr. ${doctorName} Store` : 'Medicine Store'    // shown on MY calling screen
              )}
              className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 active:scale-95 transition-transform"
              title="Voice call Medicine Store">
              <i className="fa-solid fa-phone text-green-600 text-base" />
            </button>
          )}
          {rtc.callState !== 'idle' && (
            <div className="flex items-center gap-1.5 bg-green-100 px-3 py-1.5 rounded-full">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-xs text-green-700 font-bold">
                {rtc.callState === 'ringing' ? 'Ringing...' : 'On Call'}
              </span>
            </div>
          )}
        </div>
        <div className="bg-green-50 border-b border-green-100 px-4 py-1.5 flex items-center gap-2">
          <i className="fa-solid fa-circle-info text-green-600 text-xs" />
          <p className="text-[10px] text-green-700 font-medium">Chat with Medicine Store · No time limit</p>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto py-4 px-4">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="w-8 h-8 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-3 text-center px-8">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
              <i className="fa-solid fa-pills text-green-500 text-3xl" />
            </div>
            <p className="font-bold text-text-primary">Medicine Store Chat</p>
            <p className="text-sm text-text-muted leading-relaxed">Ask about medicines, share prescriptions, or get quotes</p>
          </div>
        ) : Object.entries(grouped).map(([date, msgs]) => (
          <div key={date}>
            <DateSep date={date} />
            {msgs.map((msg, i) => (
              <Bubble key={msg._id||i} msg={msg}
                isMe={msg.from === myId || msg.senderId === myId} />
            ))}
          </div>
        ))}
        {theyTyping && (
          <div className="flex gap-1 items-center px-3 py-2 bg-white rounded-2xl rounded-bl-sm w-16 shadow-sm border border-border mb-2">
            {[0,1,2].map(i => <span key={i} className="w-1.5 h-1.5 bg-text-muted rounded-full animate-bounce" style={{animationDelay:`${i*0.15}s`}} />)}
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Call UI now rendered globally by <GlobalCallOverlay/> using the same
          shared rtc instance — no duplicate inline call screen here anymore.
          This is what fixes calls failing when this chat page is closed:
          before, this inline UI was the ONLY thing that could answer/show
          a store call, so closing the page broke calls entirely. */}

      {/* Attach panel */}
      {showAttach && (
        <>
          <div className="backdrop" onClick={() => setShowAttach(false)} />
          <div className="fixed bottom-20 left-4 right-4 z-40 bg-white rounded-2xl shadow-xl border border-border p-4">
            <div className="grid grid-cols-3 gap-3">
              {[
                {icon:'fa-image',        label:'Photo',       accept:'image/*',     color:'bg-blue-100 text-info'},
                {icon:'fa-file-pdf',     label:'Prescription',accept:'.pdf',         color:'bg-red-100 text-danger'},
                {icon:'fa-file-medical', label:'Report',      accept:'.pdf,image/*', color:'bg-purple-100 text-purple-600'},
              ].map(item => (
                <button key={item.label}
                  onClick={() => { fileRef.current.accept = item.accept; fileRef.current.click(); }}
                  className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-surface">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${item.color}`}>
                    <i className={`fa-solid ${item.icon} text-lg`} />
                  </div>
                  <span className="text-xs text-text-secondary font-medium">{item.label}</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
      <input ref={fileRef} type="file" className="hidden"
        onChange={e => { handleFileUpload(e.target.files[0]); e.target.value=''; }} />

      {/* Input bar */}
      <div className="bg-white border-t border-border px-3 py-2 safe-bottom">
        <div className="flex items-end gap-2">
          <button onClick={() => setShowAttach(!showAttach)} className="btn-icon flex-shrink-0 mb-0.5">
            {uploading
              ? <i className="fa-solid fa-spinner animate-spin text-primary" />
              : <i className="fa-solid fa-paperclip text-text-secondary text-lg" />}
          </button>
          <div className="flex-1 bg-surface border border-border rounded-2xl px-4 py-2.5 min-h-[42px] flex items-center">
            <textarea rows={1} value={text} onChange={e => handleTextChange(e.target.value)}
              onKeyDown={e => { if (e.key==='Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
              placeholder="Type a message..."
              className="flex-1 bg-transparent resize-none outline-none text-sm text-text-primary placeholder:text-text-muted max-h-28"
              style={{lineHeight:'1.4'}} />
          </div>
          <button onClick={sendMessage} disabled={!text.trim()}
            className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${text.trim()?'bg-primary':'bg-gray-100'}`}>
            <i className={`fa-solid fa-paper-plane text-sm ${text.trim()?'text-white':'text-text-muted'}`} />
          </button>
        </div>
      </div>
    </div>
  );
}
